import {
	QUERYPARAM_MODELSTATEID,
	QUERYPARAM_SETTINGSURL,
	QUERYPARAM_SLUG,
} from "@AppBuilderShared/types/shapediver/queryparams";
import {IAppBuilderUrlBuilderData} from "@AppBuilderShared/utils/urlbuilder";
import {
	IAddItemToCartData,
	IAddItemToCartReply,
	IECommerceApiActions,
	IGetParentPageInfoReply,
	IGetUserProfileReply,
	IMessageToParentData,
	IMessageToParentReply,
	IScrollingApiLoadMoreData,
	IScrollingApiLoadMoreReply,
	IScrollingApiSetParametersData,
	IScrollingApiSetParametersReply,
	IUpdateSharingLinkData,
	IUpdateSharingLinkReply,
} from "./shared/modules/ecommerce/types/ecommerceapi";
import {IScrollingApiItemTypeSelect} from "./shared/modules/ecommerce/types/scrollingapi";

// ============================================================================
// DEFAULT — TYPE DEFINITIONS
// ============================================================================

interface ICategory {
	children: ICategory[];
	id: number;
	name: string;
	path: string;
	slug: string;
	thumbnail_url: string;
}

interface IProduct {
	category_id: number;
	category_name: string;
	category_structure: Array<string>;
	description: string;
	downloadable_files: {
		height: number;
		image_size: string;
		keep_horizontal_orientation: boolean;
		keep_vertical_orientation: boolean;
		parameterValues: {
			file_url: string;
		};
		sessionId: Array<string>;
		width: number;
	}[];
	graphic_id: number;
	graphic_name: string;
	tags: string[];
	thumbnail_url?: string;
	image_url?: string;
}

interface IGraphicsApiResponse {
	categories?: ICategory[];
	products?: IProduct[];
}

// ============================================================================
// DEFAULT — MODULE-LEVEL VARIABLES & FUNCTIONS (Graphics API)
// ============================================================================

function getGraphicsApiQueryString(
	categories?: string[] | string,
	tags?: string,
	search?: string,
	categoryIds?: number[],
): string {
	const params = new URLSearchParams();
	if (Array.isArray(categories) && categories.length === 0)
		categories = undefined;

	if (!categories && !tags && !search) categories = "all";

	if (categoryIds && categoryIds.length > 0) {
		params.set(
			"category_id",
			categoryIds[categoryIds.length - 1].toString(),
		);
		if (!tags && !search) {
			params.set("tags", "all");
		}
	} else if (categories) {
		if (Array.isArray(categories))
			params.set("categories", categories.join(","));
		else params.set("categories", categories);
		if (categories !== "all" && !tags && !search) {
			params.set("tags", "all");
		}
	}
	if (tags) params.set("tags", tags);
	if (search) params.set("search", search);

	return params.toString();
}

let latestQuery: string = getGraphicsApiQueryString("");
const allCategoriesQuery: string = getGraphicsApiQueryString();
let latestCategorySlugs: Array<string> = [];
let pageSize: number = 10;
let currentIndex: number = 0;
const cachedResults: Record<string, IGraphicsApiResponse> = {};
const categoryIdentifier = "\u200B\u2063";
let resetCacheCallback: (() => void) | null = null;

function clearCache() {
	latestQuery = getGraphicsApiQueryString();
	latestCategorySlugs = [];
	pageSize = 10;
	currentIndex = 0;
	for (const key in cachedResults) delete cachedResults[key];
	if (resetCacheCallback) resetCacheCallback();
}

let cachedNonce: string = "";
let nonceFetchedAt: number = 0;
const NONCE_TTL_MS = 10 * 60 * 1000; // 10 minutes

async function getFreshNonce(): Promise<string> {
    // 1. Use window-provided nonce if available and recent
    const windowNonce = (window as any).GC_MY_IMAGES?.nonce 
        || (window as any).shapediverAjax?.nonce 
        || (window as any).wpApiSettings?.nonce 
        || "";

    if (windowNonce) return windowNonce;

    // 2. Use cached nonce if still fresh
    const now = Date.now();
    if (cachedNonce && (now - nonceFetchedAt) < NONCE_TTL_MS) {
        return cachedNonce;
    }

    // 3. Fetch a fresh nonce from WP REST API
    try {
        const resp = await fetch("/wp-json/", {
            method: "GET",
            credentials: "include",
        });
        const data = await resp.json();
        // WP REST API root exposes nonce via wp-auth-check or we use admin-ajax
        const freshNonce = data?.nonce || "";
        if (freshNonce) {
            cachedNonce = freshNonce;
            nonceFetchedAt = now;
            return freshNonce;
        }
    } catch (e) {
        // silent
    }

    // 4. Fallback: fetch nonce via admin-ajax
    try {
        const resp = await fetch("/wp-admin/admin-ajax.php", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({ action: "shapediver_check_login_status" }),
        });
        const data = await resp.json();
        const freshNonce = data?.data?.nonce || "";
        if (freshNonce) {
            cachedNonce = freshNonce;
            nonceFetchedAt = now;
            return freshNonce;
        }
    } catch (e) {
        // silent
    }

    return cachedNonce || "";
}

async function fetchFromGraphicsApi(
	queryString: string,
	ignoreCache: boolean,
): Promise<IGraphicsApiResponse> {
	if (!ignoreCache && cachedResults[queryString]) {
		return cachedResults[queryString];
	}

	
    // const base = `${configuratorData.api_base_url}/wp-json/shapediver/v1/graphic-components`;

	const base =
    `https://dev4.tarablooms.in/wp-json/shapediver/v1/graphic-components`;

	const url = `${base}?${queryString}`;

	const nonce = await getFreshNonce();


	const resp = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: {
			Accept: "application/json",
			...(nonce ? {"X-WP-Nonce": nonce} : {}),
		},
	});

	if (!resp.ok) {
		const text = await resp.text().catch(() => "");
		throw new Error(
			`Failed to fetch from graphics api: ${resp.status} ${resp.statusText} ${text}`,
		);
	}

	const json = await resp.json().catch((err) => {
		throw new Error(
			`Failed to parse graphics api response: ${String(err)}`,
		);
	});

	if (!json || typeof json !== "object")
		throw new Error("Invalid graphics api response: not an object");

	const result = json as IGraphicsApiResponse;
	cachedResults[queryString] = result;
	return result;
}

async function returnAndMapCachedResults(): Promise<IScrollingApiLoadMoreReply<unknown>> {

	const result: IScrollingApiLoadMoreReply<unknown> = {
		hasNextPage: false,
		items: [],
	};

	const activeParams      = new URLSearchParams(latestQuery);
	const hasSearch         = activeParams.has("search") && activeParams.get("search") !== "";
	const hasTags           = activeParams.has("tags")   && activeParams.get("tags")   !== "all";
	const shouldShowFolders = currentIndex === 0 && !hasSearch && !hasTags;

	if (shouldShowFolders) {
		const categoryData = await fetchFromGraphicsApi(allCategoriesQuery, false);
		let categories = categoryData.categories;

		if (categories) {
			for (let i = 0; i < latestCategorySlugs.length; i++) {
				const categorySlug = latestCategorySlugs[i];
				const matchedCategory: ICategory | undefined = categories.find(
					(cat) => cat.slug === categorySlug,
				);
				if (!matchedCategory)
					throw new Error(
						`Category with slug "${categorySlug}" not found`,
					);
				categories = matchedCategory.children;
			}

			const categoryItems: IScrollingApiItemTypeSelect[] = categories.map((p) => ({
				item: `search:${categoryIdentifier}` + p.name,
				data: {
					displayname: p.name,
					imageUrl: p.thumbnail_url,
					data: p,
				},
			}));
			result.items = categoryItems;
		}
	}

	const products = cachedResults[latestQuery]?.products;
	if (products) {
		const productItems: IScrollingApiItemTypeSelect[] = products
			.slice(currentIndex, currentIndex + pageSize)
			.map((p) => ({
				item: p.graphic_id + "",
				data: {
					displayname: "\u200B\u2063",
					tooltip: p.description,
					imageUrl: p.downloadable_files.find((f) =>
						f.sessionId.includes("thumbnail"),
					)?.parameterValues.file_url
						?? p.thumbnail_url
						?? p.image_url,
					data: p,
				},
			}));
		currentIndex += pageSize;

		result.hasNextPage = currentIndex < products.length;
		result.items = result.items.concat(productItems);
	}

	return result;
}

async function scrollingApiSetParameters(
	data: IScrollingApiSetParametersData,
): Promise<IScrollingApiSetParametersReply<unknown>> {
	if (data.source !== "graphics") {
		console.debug(`Unsupported data source name: ${data.source}`);
		clearCache();
		return {hasNextPage: false, items: []};
	}

	if (data.reset) {
		resetCacheCallback = data.reset;
	}

	if (data.terms) {
		const categories: string[] = [];
		const categoryIds: number[] = [];
		let tags: string = "";
		let search: string = "";
		const categoryData = await fetchFromGraphicsApi(
			allCategoriesQuery,
			false,
		);
		let cachedCategories = categoryData.categories;
		data.terms?.forEach((v) => {
			if (v.startsWith(categoryIdentifier)) {
				if (cachedCategories) {
					const categoryName = v.substring(categoryIdentifier.length);
					const matchedCategory = cachedCategories.find(
						(cat) => cat.name === categoryName,
					);
					if (matchedCategory) {
						categories.push(matchedCategory.slug);
						categoryIds.push(matchedCategory.id);
						cachedCategories = matchedCategory.children;
					}
				}
			} else if (v.startsWith("tag:")) tags = v.substring("tag:".length);
			else search = v;
		});
		const queryString = getGraphicsApiQueryString(
			categories,
			tags,
			search,
			categoryIds,
		);
		if (queryString !== latestQuery) {
			latestQuery = queryString;
			latestCategorySlugs = categories;
			currentIndex = 0;
		}
	}

	if (data.pageSize !== undefined) {
		pageSize = data.pageSize;
		currentIndex = 0;
	}

	await fetchFromGraphicsApi(latestQuery, false);
	return returnAndMapCachedResults();
}

async function scrollingApiLoadMore(
	data: IScrollingApiLoadMoreData,
): Promise<IScrollingApiLoadMoreReply<unknown>> {
	if (data.source !== "graphics") {
		console.debug(`Unsupported data source name: ${data.source}`);
		clearCache();
		return {hasNextPage: false, items: []};
	}

	await fetchFromGraphicsApi(latestQuery, false);
	return returnAndMapCachedResults();
}

// ============================================================================
// DEFAULT — SpecificECommerceApiActions CLASS
// ============================================================================

class SpecificECommerceApiActions implements IECommerceApiActions {
	private productDataManager: ProductDataManager;
	private graphicsApiManager: GraphicsApiManager;

	constructor(private defaultActions?: IECommerceApiActions) {
		this.productDataManager = ProductDataManager.getInstance();
		this.graphicsApiManager = GraphicsApiManager.getInstance();
	}

	getParentPageInfo(): Promise<IGetParentPageInfoReply> {
		if (this.defaultActions) return this.defaultActions.getParentPageInfo();
		return Promise.resolve({href: window.location.href});
	}

	closeConfigurator(): Promise<boolean> {
		if (this.defaultActions) return this.defaultActions.closeConfigurator();

		return Promise.resolve(false);
	}

	addItemToCart(data: IAddItemToCartData): Promise<IAddItemToCartReply> {
    const {description, modelStateId, imageUrl} = data;

    console.log("🛒 ADD ITEM TO CART | Model State ID:", modelStateId);

    if (imageUrl) (window as any).shapeDiverImageUrl = imageUrl;

    let pricingParameters: Record<string, any> = {};
    if (description) {
        try {
            pricingParameters = JSON.parse(description);
        } catch (e) {
            console.warn(`Failed to parse pricing parameters from description: ${description}`, e);
        }
    }

    const doAddToCart = async (): Promise<IAddItemToCartReply> => {

        // ── Resolve product data (with lazy-load fallback) ─────────────
        let productData = this.productDataManager.getProductData();

        if (!productData) {
            console.warn("⚠️ Product data not in cache — attempting lazy load...");

            // Try reading product ID from the configurator button
            const btn = (
                document.getElementById("custom-sd-open-btn") ||
                document.getElementById("app-builder-open-configurator")
            ) as HTMLElement | null;
            const productIdAttr = btn?.getAttribute("data-product-id");

            if (productIdAttr) {
                productData = await this.productDataManager.loadProductData(productIdAttr);
            }

            // Still null? Try window fallback one more time
            if (!productData) {
                productData = this.productDataManager.getProductData();
            }
        }

        if (!productData) {
            console.error("❌ No product data available after lazy load!");
            alert("Product information missing. Please close the configurator, refresh the page and try again.");
            return {id: modelStateId || "ERROR"};
        }
        // ───────────────────────────────────────────────────────────────

        pricingParameters.product_id     = productData.id;
        pricingParameters.variation_id   = productData.variation_id   || 0;
        pricingParameters.variation_name = productData.variation_name || "";
        pricingParameters.model_state_id = modelStateId || productData.model_state_id;

        pricingParameters.sd_configurator_url =
            developmentUrlBuilderOptions.baseUrl || "";
        pricingParameters.sd_slug =
            productData.slug || "";
        pricingParameters.sd_settings_url =
            productData.settings_url || "";
        pricingParameters.sd_parameters_settings_url =
            (productData as any).query_params?._parameters_settings_url || "";

        if (!pricingParameters.product_id) {
            console.error("❌ CRITICAL: No product_id in pricing parameters!");
            alert("Product information missing. Please refresh the page and try again.");
            return {id: modelStateId || "ERROR"};
        }

        const validModelStateId = modelStateId || "TEMP_STATE_ID";

        // ── Family name logic (unchanged) ───────────────────────────────
        let familyName = ((window as any).shapeDiverFamilyName as string | undefined)?.trim();

        if (!familyName && modelStateId) {
			const cartTbplUid = (window as any).shapeDiverTbplUid || "";
			familyName = await this.fetchExistingDesignTitle(modelStateId, cartTbplUid);
			if (familyName) (window as any).shapeDiverFamilyName = familyName;
		}

        if (!familyName) {
            console.log("📝 No family name — prompting before add to cart...");
            const entered = await this.promptFamilyNameModal();
            if (!entered) {
                console.warn("⚠️ User cancelled family name — add to cart aborted");
                return {id: validModelStateId};
            }
            familyName = entered;
            (window as any).shapeDiverFamilyName = familyName;

            const url = new URL(window.location.href);
            if (modelStateId) url.searchParams.set("modelStateId", modelStateId);
            const href = url.toString();
            const savedImageUrl = (window as any).shapeDiverImageUrl || "";
            const originalModelStateId = new URL(window.location.href).searchParams.get("modelStateId") || null;

            try {
                await this.saveDesign(
                    modelStateId || validModelStateId,
                    href,
                    familyName,
                    savedImageUrl,
                    originalModelStateId,
                    true,
                );
                console.log("✅ Family name saved silently before cart");
            } catch (err) {
                console.warn("⚠️ Silent save failed (continuing to cart anyway):", err);
            }
        }

        pricingParameters.design_family_name = familyName;

        const loadingOverlay = document.getElementById("warehouse-loading-overlay");
        if (loadingOverlay) loadingOverlay.style.display = "flex";

        return this.fetchPricingWithRetry(pricingParameters)
            .then((apiResponse) => {
                if (loadingOverlay) loadingOverlay.style.display = "none";
 
                if (apiResponse && apiResponse.warehouses && apiResponse.warehouses.length > 0) {
 
                    // ── Zero-price check ─────────────────────────────────────────
                    // If EVERY warehouse has total_cost === 0 (or missing/undefined)
                    // this is a custom / manual-pricing scenario.
                    // Show the "request a price" popup instead of the warehouse popup.
                    const allZero: boolean = apiResponse.warehouses.every((w: any) => {
                        const pricing = w.markup_pricing_summary || w.pricing_summary || {};
                        const total   = Number(pricing.total_cost);
                        return !total || total === 0;
                    });
 
                    if (allZero) {
                        console.log("💰 All warehouses return zero cost — showing price-enquiry popup");
 
                        // Resolve display values for the popup.
                        const productName: string =
                            productData?.name ||
                            document.title   ||
                            "";
 
                        const productUrl: string = window.location.href;
 
                        // sdwpShowZeroPricePopup is defined in zero-price-popup.php
                        // which is now injected on EVERY front-end page at wp_footer
                        // priority 20 (after Scripts::inject_ajax_config at 5).
                        //
                        // If somehow not available yet (very rare race), fall back
                        // to a DOM-safe inline popup creation — never alert().
                        if (typeof (window as any).sdwpShowZeroPricePopup === "function") {
                            (window as any).sdwpShowZeroPricePopup(
                                productName,
                                productUrl,
                                validModelStateId,
                            );
                        } else {
                            // Last-resort fallback — should not happen in production.
                            // Retry after 300ms in case the script loaded late.
                            console.warn("sdwpShowZeroPricePopup not found — retrying in 300ms");
                            setTimeout(() => {
                                if (typeof (window as any).sdwpShowZeroPricePopup === "function") {
                                    (window as any).sdwpShowZeroPricePopup(
                                        productName,
                                        productUrl,
                                        validModelStateId,
                                    );
                                } else {
                                    console.error("sdwpShowZeroPricePopup still unavailable — check plugin activation");
                                }
                            }, 300);
                        }
 
                        return {id: validModelStateId};
                    }
                    // ── End zero-price check ─────────────────────────────────────
 
                    // Normal path — at least one warehouse has a real price.
                    WarehouseManager.openPopup(
                        apiResponse.warehouses,
                        pricingParameters,
                        validModelStateId,
                        productData,
                        apiResponse,
                    );
                    return {id: validModelStateId};
 
                } else {
                    throw new Error("No warehouse data available");
                }
            })
            .catch((error: any) => {
                if (loadingOverlay) loadingOverlay.style.display = "none";
                const errorMessage = error.message || "Unable to load pricing information. Please try again later.";
                console.error("❌ FINAL ERROR:", errorMessage);
                alert(`Unable to load pricing information.\n\nError: ${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
                return {id: validModelStateId};
            });
    };

    return doAddToCart();
}

	getUserProfile(): Promise<IGetUserProfileReply> {
		if (this.defaultActions) return this.defaultActions.getUserProfile();

		const reply: IGetUserProfileReply = {
			id: "DUMMY_ID",
			email: "john@doe.com",
			name: "John Doe",
		};

		return Promise.resolve(reply);
	}

	updateSharingLink(
		data: IUpdateSharingLinkData,
	): Promise<IUpdateSharingLinkReply> {
		const {modelStateId, imageUrl} = data;

		const url = new URL(window.location.href);
		const originalModelStateId = url.searchParams.get(QUERYPARAM_MODELSTATEID);

		// ── Capture tbpl_uid from URL before we mutate it ──────────────────
		// This is set by MyDesigns "Configure" button via add_query_arg().
		// It uniquely identifies which DB record to update — safe even when
		// multiple designs share the same modelStateId (e.g. after cloning).
		const tbplUid = url.searchParams.get("_tbpl_uid") || "";
		if (tbplUid) (window as any).shapeDiverTbplUid = tbplUid;
		// ───────────────────────────────────────────────────────────────────

		url.searchParams.set(QUERYPARAM_MODELSTATEID, modelStateId);
		const href = url.toString();
		history.replaceState(history.state, "", href);

		console.log("💾 Silent save triggered | New ID:", modelStateId, "| tbpl_uid:", tbplUid || "(none — new design)");

		const doSave = async (): Promise<IUpdateSharingLinkReply> => {

			const isLoggedIn: boolean = (window as any).shapediverAjax?.isLoggedIn === true
				|| configuratorData?.isLoggedIn === true;

			if (!isLoggedIn) {
				console.log("🔒 User not logged in — opening login in new tab");

				const currentUrl = new URL(window.location.href);
				currentUrl.searchParams.set(QUERYPARAM_MODELSTATEID, modelStateId);
				const urlWithState = currentUrl.toString();

				history.replaceState(history.state, "", urlWithState);

				try {
					sessionStorage.setItem('shapediver_pending_save', JSON.stringify({
						modelStateId,
						imageUrl: imageUrl || (window as any).shapeDiverImageUrl || "",
						href: urlWithState,
					}));
				} catch (e) {}

				const myAccountBase: string = ((window as any).configuratorData?.loginUrl || `${window.location.origin}/my-account/`).split('?')[0];
				const loginUrl = `${myAccountBase}?redirect_to=${encodeURIComponent(window.location.origin + '/config-login-success/')}`;
				const loginTab = window.open(loginUrl, '_blank');

				return new Promise<IUpdateSharingLinkReply>((resolve) => {
					const CHECK_INTERVAL = 1500;
					const MAX_WAIT = 5 * 60 * 1000;
					let elapsed = 0;

					const checkLogin = setInterval(async () => {
						elapsed += CHECK_INTERVAL;

						if (loginTab && loginTab.closed && elapsed < 5000) {
							clearInterval(checkLogin);
							console.warn("⚠️ Login tab closed early — aborting save");
							resolve({ href });
							return;
						}

						try {
							const resp = await fetch((window as any).configuratorData?.ajaxurl || '/wp-admin/admin-ajax.php', {
								method: 'POST',
								credentials: 'same-origin',
								headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
								body: new URLSearchParams({ action: 'shapediver_check_login_status' }),
							});
							const result = await resp.json();

							if (result.success && result.data?.isLoggedIn) {
								clearInterval(checkLogin);
								console.log("✅ Login detected! User:", result.data.display_name);

								if ((window as any).shapediverAjax) {
									(window as any).shapediverAjax.isLoggedIn = true;
								}
								if ((window as any).configuratorData) {
									(window as any).configuratorData.isLoggedIn = true;
								}

								try { if (loginTab && !loginTab.closed) loginTab.close(); } catch(e) {}

								try { sessionStorage.removeItem('shapediver_pending_save'); } catch(e) {}

								// Try to prefill from existing saved design
								let prefillTitle = "";
								if (modelStateId) {
									prefillTitle = await this.fetchExistingDesignTitle(modelStateId, tbplUid);
								}
								// Also try the cached latest family name as fallback
								if (!prefillTitle) {
								prefillTitle = ((window as any).shapeDiverFamilyName as string | undefined)?.trim() || "";
								}
								if (!prefillTitle) {
								prefillTitle = cachedLatestFamilyName || "";
								}

								// Prefill the window variable so promptFamilyNameModal can use it
								if (prefillTitle) {
								(window as any).shapeDiverFamilyName = prefillTitle;
								}

								const familyName = await this.promptFamilyNameModal();
								if (!familyName) {
								console.warn("⚠️ User cancelled family name — save skipped");
								resolve({ href });
								return;
								}

								(window as any).shapeDiverFamilyName = familyName;

								await this.saveDesign(
									modelStateId,
									urlWithState,
									familyName,
									imageUrl || (window as any).shapeDiverImageUrl,
									null,
								);

								resolve({ href: urlWithState });
							}
						} catch (e) {
							console.warn("⚠️ Login poll error:", e);
						}

						if (elapsed >= MAX_WAIT) {
							clearInterval(checkLogin);
							console.warn("⚠️ Login wait timeout — aborting");
							try { if (loginTab && !loginTab.closed) loginTab.close(); } catch(e) {}
							resolve({ href });
						}
					}, CHECK_INTERVAL);
				});
			}

			if (imageUrl) {
				(window as any).shapeDiverImageUrl = imageUrl;
				console.log("🖼️ Uploading image to server...");
				try {
					const uploadedUrl = await this.uploadBase64Image(imageUrl, modelStateId);
					if (uploadedUrl) {
						(window as any).shapeDiverImageUrl = uploadedUrl;
						console.log("✅ Image uploaded, server URL stored:", uploadedUrl);
					}
				} catch (err) {
					console.warn("⚠️ Image upload failed, using base64 fallback:", err);
				}
			}

			let designTitle = ((window as any).shapeDiverFamilyName as string | undefined)?.trim();

			if (!designTitle && originalModelStateId) {
				designTitle = await this.fetchExistingDesignTitle(originalModelStateId, tbplUid);
				if (designTitle) (window as any).shapeDiverFamilyName = designTitle;
			}

			if (!designTitle) {
				console.log("📝 No family name found — prompting user via modal...");
				designTitle = await this.promptFamilyNameModal();
				if (designTitle) {
					(window as any).shapeDiverFamilyName = designTitle;
				} else {
					console.warn("⚠️ User cancelled family name input — save skipped");
					return {href};
				}
			}

			await this.saveDesign(
				modelStateId,
				href,
				designTitle,
				(window as any).shapeDiverImageUrl,
				originalModelStateId,
			);

			return {href};
		};

		return doSave();
	}

	async scrollingApiSetParameters(
		data: IScrollingApiSetParametersData,
	): Promise<IScrollingApiSetParametersReply<unknown>> {
		return scrollingApiSetParameters(data);
	}

	async scrollingApiLoadMore(
		data: IScrollingApiLoadMoreData,
	): Promise<IScrollingApiLoadMoreReply<unknown>> {
		return scrollingApiLoadMore(data);
	}

	async messageToParent(
    data: IMessageToParentData,
): Promise<IMessageToParentReply> {
    if (data.type === "uploadImages") {

        // ── CHECK LOGIN BEFORE SHOWING UPLOAD MODAL ────────────────────
        const isLoggedIn: boolean = (window as any).shapediverAjax?.isLoggedIn === true
            || (window as any).configuratorData?.isLoggedIn === true;

			if (!isLoggedIn) {
    console.log("🔒 Not logged in — opening login tab before image upload");

    try {
        sessionStorage.setItem('shapediver_pending_upload', JSON.stringify({
            intent: 'uploadImages',
            timestamp: Date.now(),
        }));
    } catch (e) {}

    const myAccountBase: string = (
        (window as any).configuratorData?.loginUrl
        || `${window.location.origin}/my-account/`
    ).split('?')[0];

    const loginUrl = `${myAccountBase}?redirect_to=${encodeURIComponent(
        window.location.origin + '/config-login-success/'
    )}`;

    const loginTab = window.open(loginUrl, '_blank');

    return new Promise<IMessageToParentReply>((resolve) => {
        const CHECK_INTERVAL = 1500;
        const MAX_WAIT = 5 * 60 * 1000;
        let elapsed = 0;
        let alreadyResolved = false; // ← GUARD FLAG

        const checkLogin = setInterval(async () => {
            // ── If already handled, just ensure interval is cleared ────
            if (alreadyResolved) {
                clearInterval(checkLogin);
                return;
            }

            elapsed += CHECK_INTERVAL;

            if (loginTab && loginTab.closed && elapsed < 5000) {
                if (alreadyResolved) return;
                alreadyResolved = true;
                clearInterval(checkLogin);
                console.warn("⚠️ Login tab closed early — upload aborted");
                try { sessionStorage.removeItem('shapediver_pending_upload'); } catch (e) {}
                resolve({ notification: { data: { message: "Login cancelled." } } });
                return;
            }

            try {
                const resp = await fetch(
                    (window as any).configuratorData?.ajaxurl || '/wp-admin/admin-ajax.php',
                    {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams({ action: 'shapediver_check_login_status' }),
                    }
                );
                const result = await resp.json();

                if (result.success && result.data?.isLoggedIn) {
                    // ── GUARD: only proceed once ───────────────────────
                    if (alreadyResolved) return;
                    alreadyResolved = true;
                    clearInterval(checkLogin); // ← clear BEFORE await
                    // ──────────────────────────────────────────────────

                    console.log("✅ Login detected — proceeding with upload");

                    if ((window as any).shapediverAjax) {
                        (window as any).shapediverAjax.isLoggedIn = true;
                    }
                    if ((window as any).configuratorData) {
                        (window as any).configuratorData.isLoggedIn = true;
                    }

                    try { if (loginTab && !loginTab.closed) loginTab.close(); } catch (e) {}
                    try { sessionStorage.removeItem('shapediver_pending_upload'); } catch (e) {}

                    // ── Now safe to await imageUpload ──────────────────
                    const success = await imageUpload();
                    if (success) clearCache();

                    resolve({
                        notification: {
                            data: {
                                message: success
                                    ? "Image uploaded successfully."
                                    : "Image upload cancelled or failed.",
                            },
                        },
                    });
                }
            } catch (e) {
                console.warn("⚠️ Login poll error:", e);
            }

            if (!alreadyResolved && elapsed >= MAX_WAIT) {
                alreadyResolved = true;
                clearInterval(checkLogin);
                console.warn("⚠️ Login wait timeout — upload aborted");
                try { if (loginTab && !loginTab.closed) loginTab.close(); } catch (e) {}
                try { sessionStorage.removeItem('shapediver_pending_upload'); } catch (e) {}
                resolve({ notification: { data: { message: "Login timed out." } } });
            }
        }, CHECK_INTERVAL);
    });
}
        // ── Already logged in — proceed directly ───────────────────────

        const success = await imageUpload();
        if (success) clearCache();
        return Promise.resolve({
            notification: {
                data: {
                    message: success
                        ? "Image uploaded successfully."
                        : "Image upload cancelled or failed.",
                },
            },
        });
    }
    return Promise.resolve({});
}

	// --------------------------------------------------------------------------
	// Private helpers
	// --------------------------------------------------------------------------

	private async fetchPricingWithRetry(pricingParameters: Record<string, any>): Promise<any> {
		let lastError: any = null;

		console.log("🔧 pricingParameters:", JSON.stringify(pricingParameters, null, 2));

		for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
			try {
				console.log(`📤 Calling wrapper_pricing API (Attempt ${attempt + 1}/${MAX_RETRIES + 1})`);

				const apiUrl = `${window.location.origin}/wp-json/shapediver/v1/wrapper_pricing`;
				const requestBody = {type: "Pricing", data: pricingParameters};

				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

				const response = await fetch(apiUrl, {
					method: "POST",
					headers: {"Content-Type": "application/json"},
					credentials: "same-origin",
					body: JSON.stringify(requestBody),
					signal: controller.signal,
				});

				clearTimeout(timeoutId);

				if (response.ok) {
					const result = await response.json();
					console.log("✅ Wrapper pricing response:", result);
					return result;
				} else {
					const errorText = await response.text();
					let errorData;
					try { errorData = JSON.parse(errorText); } catch (e) { errorData = {message: errorText}; }

					console.error("❌ API Error:", response.status, errorData);

					if (errorData.message && errorData.message.includes("Timeout")) {
						lastError = new Error("Connection timeout. The pricing server is not responding.");
						if (attempt < MAX_RETRIES) {
							console.log(`⏳ Retrying in ${RETRY_DELAY / 1000} seconds...`);
							await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
							continue;
						}
					} else {
						lastError = new Error(errorData.message || "Failed to load pricing information");
						break;
					}
				}
			} catch (error: any) {
				console.error("❌ Network error:", error);
				lastError = error.name === "AbortError"
					? new Error("Request timeout. Please try again.")
					: error;

				if (attempt < MAX_RETRIES) {
					console.log(`⏳ Retrying in ${RETRY_DELAY / 1000} seconds...`);
					await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
					continue;
				}
			}

			break;
		}

		throw lastError;
	}

	private async uploadBase64Image(base64ImageUrl: string, modelStateId: string): Promise<string | null> {
		try {
			const response = await fetch(base64ImageUrl);
			const blob = await response.blob();

			const fileName = `shapediver_${modelStateId}_${Date.now()}.png`;
			const file = new File([blob], fileName, {type: "image/png"});

			const formData = new FormData();
			formData.append("file", file);
			formData.append("model_state_id", modelStateId);
			formData.append("type", "configurator_snapshot");

			const productData = this.productDataManager.getProductData();
			if (productData) formData.append("product_id", productData.id.toString());

			const apiUrl = `${window.location.origin}/wp-json/shapediver/v1/upload-configurator-image`;

			const uploadResponse = await fetch(apiUrl, {
				method: "POST",
				body: formData,
				credentials: "same-origin",
			});

			if (!uploadResponse.ok) throw new Error(`HTTP ${uploadResponse.status}`);

			const result = await uploadResponse.json();
			return result.success && result.data?.url ? result.data.url : null;
		} catch (error) {
			console.error("❌ Image upload error:", error);
			return null;
		}
	}

	private async fetchExistingDesignTitle(modelStateId: string, tbplUid?: string): Promise<string> {
		try {
			const formData = new URLSearchParams();
			formData.append("action", "get_shapediver_design_by_state_id");
			formData.append("modelStateId", modelStateId);

			const uid = tbplUid || (window as any).shapeDiverTbplUid || "";
        	if (uid) formData.append("tbpl_uid", uid);

			const nonce = (window as any).shapediverAjax?.nonce;
			if (nonce) formData.append("nonce", nonce);

			const response = await fetch("/wp-admin/admin-ajax.php", {
				method: "POST",
				credentials: "same-origin",
				headers: {"Content-Type": "application/x-www-form-urlencoded"},
				body: formData.toString(),
			});

			if (response.ok) {
				const result = await response.json();
				if (result.success && result.data?.designTitle) return result.data.designTitle;
			}
		} catch (error) {
			console.error("❌ Fetch existing title error:", error);
		}
		return "";
	}

	private showSaveModal(existingTitle: string = ""): Promise<string | null> {
		return new Promise((resolve) => {
			const modal = document.getElementById("shapediver-save-modal");
			const input = document.getElementById("design-title") as HTMLInputElement;

			if (!modal || !input) { resolve(null); return; }

			const errorDiv = modal.querySelector(".shapediver-error-message") as HTMLElement;
			const submitBtn = modal.querySelector(".shapediver-btn-submit") as HTMLButtonElement;
			const cancelBtn = modal.querySelector(".shapediver-btn-cancel") as HTMLButtonElement;
			const closeBtn = modal.querySelector(".shapediver-modal-close") as HTMLButtonElement;

			input.value = existingTitle.trim() !== "" ? existingTitle : "";
			if (errorDiv) errorDiv.style.display = "none";
			if (submitBtn) submitBtn.disabled = false;

			modal.style.display = "flex";
			setTimeout(() => { input.focus(); input.select(); }, 100);

			const hideModal = () => { modal.style.display = "none"; };

			const handleSubmit = () => {
				const title = input.value.trim();
				if (!title || title.length < 3) {
					if (errorDiv) {
						errorDiv.textContent = title ? "Title must be at least 3 characters" : "Please enter a design title";
						errorDiv.style.display = "block";
					}
					return;
				}
				hideModal(); cleanup(); resolve(title);
			};

			const handleCancel = () => { hideModal(); cleanup(); resolve(null); };

			const handleKeyPress = (e: KeyboardEvent) => {
				if (e.key === "Enter") { e.preventDefault(); handleSubmit(); }
				else if (e.key === "Escape") { handleCancel(); }
			};

			const handleBackdropClick = (e: MouseEvent) => { if (e.target === modal) handleCancel(); };

			const cleanup = () => {
				if (submitBtn) submitBtn.removeEventListener("click", handleSubmit);
				if (cancelBtn) cancelBtn.removeEventListener("click", handleCancel);
				if (closeBtn) closeBtn.removeEventListener("click", handleCancel);
				input.removeEventListener("keypress", handleKeyPress);
				modal.removeEventListener("click", handleBackdropClick);
			};

			if (submitBtn) submitBtn.addEventListener("click", handleSubmit);
			if (cancelBtn) cancelBtn.addEventListener("click", handleCancel);
			if (closeBtn) closeBtn.addEventListener("click", handleCancel);
			input.addEventListener("keypress", handleKeyPress);
			modal.addEventListener("click", handleBackdropClick);
		});
	}

	private promptFamilyNameModal(): Promise<string | undefined> {
		return new Promise((resolve) => {
			const modal    = document.getElementById("shapediver-save-modal");
			const input    = document.getElementById("design-title") as HTMLInputElement | null;
			const errorDiv = modal?.querySelector(".shapediver-error-message") as HTMLElement | null;
			const submitBtn = modal?.querySelector(".shapediver-btn-submit") as HTMLElement | null;
			const cancelBtn = modal?.querySelector(".shapediver-btn-cancel") as HTMLElement | null;
			const closeBtn  = modal?.querySelector(".shapediver-modal-close") as HTMLElement | null;

			if (!modal || !input) {
				const name = window.prompt("Enter a family name for your design:");
				resolve(name && name.trim().length >= 3 ? name.trim() : undefined);
				return;
			}

			// Prefill from window.shapeDiverFamilyName if available
			const prefill = ((window as any).shapeDiverFamilyName as string | undefined)?.trim() || "";
			input.value = prefill;
			if (errorDiv) errorDiv.style.display = "none";

			modal.style.display = "flex";
			setTimeout(() => { input.focus(); input.select(); }, 100);

			const hideModal = () => { modal.style.display = "none"; };

			const cleanup = () => {
				submitBtn?.removeEventListener("click", handleSubmit);
				cancelBtn?.removeEventListener("click", handleCancel);
				closeBtn?.removeEventListener("click", handleCancel);
				input.removeEventListener("keydown", handleKey);
				modal.removeEventListener("click", handleBackdrop);
			};

			const handleSubmit = () => {
				const title = input.value.trim();
				if (!title || title.length < 3) {
					if (errorDiv) {
						errorDiv.textContent = title
							? "Name must be at least 3 characters"
							: "Please enter a family name";
						errorDiv.style.display = "block";
					}
					return;
				}
				hideModal();
				cleanup();
				resolve(title);
			};

			const handleCancel = () => {
				hideModal();
				cleanup();
				resolve(undefined);
			};

			const handleKey = (e: KeyboardEvent) => {
				if (e.key === "Enter")  { e.preventDefault(); handleSubmit(); }
				if (e.key === "Escape") { handleCancel(); }
			};

			const handleBackdrop = (e: MouseEvent) => {
				if (e.target === modal) handleCancel();
			};

			submitBtn?.addEventListener("click", handleSubmit);
			cancelBtn?.addEventListener("click", handleCancel);
			closeBtn?.addEventListener("click", handleCancel);
			input.addEventListener("keydown", handleKey);
			modal.addEventListener("click", handleBackdrop);
		});
	}

	private async saveDesign(
		modelStateId: string,
		url: string,
		designTitle: string,
		imageUrl?: string,
		originalModelStateId?: string | null,
		silent: boolean = false,
	): Promise<void> {
		const saveFormData = new FormData();
		saveFormData.append("action", "save_shapediver_model_state");
		saveFormData.append("modelStateId", modelStateId);
		saveFormData.append("url", url);
		saveFormData.append("designTitle", designTitle);
		

		const productData = this.productDataManager.getProductData();
		if (productData) {
			saveFormData.append("product_id", productData.id.toString());
			if (productData.variation_id)
				saveFormData.append("variation_id", productData.variation_id.toString());
			if (productData.variation_name)
				saveFormData.append("variation_name", productData.variation_name);
		}

		if (imageUrl) saveFormData.append("imageUrl", imageUrl);

		if (originalModelStateId && originalModelStateId.trim() !== "") {
			saveFormData.append("originalModelStateId", originalModelStateId);
			console.log("🔄 Updating existing design. Original ID:", originalModelStateId, "→ New ID:", modelStateId);
		} else {
			console.log("➕ Inserting new design. ID:", modelStateId);
		}

		const nonce = (window as any).shapediverAjax?.nonce;
		if (nonce) saveFormData.append("nonce", nonce);

		// ── Send tbpl_uid so PHP can find the exact DB record to update ────
		// Priority: window cache (set in updateSharingLink) → URL param fallback.
		const tbplUid: string =
			( (window as any).shapeDiverTbplUid as string | undefined )?.trim() ||
			new URL(window.location.href).searchParams.get("_tbpl_uid") ||
			"";
		if (tbplUid) saveFormData.append("tbpl_uid", tbplUid);
		// ───────────────────────────────────────────────────────────────────

		try {
			const response = await fetch("/wp-admin/admin-ajax.php", {
				method: "POST",
				credentials: "same-origin",
				body: saveFormData,
			});

			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

			const saveResult = await response.json();

			if (saveResult.success) {
				const action = saveResult.data?.action || "saved";
				console.log(`✅ Design ${action} successfully`);
				if (!silent) this.showSuccessModal();
			} else {
				console.error("❌ Save failed:", saveResult.data);
			}
		} catch (error) {
			console.error("❌ Save error:", error);
		}
	}

	private showSuccessModal(): void {
		const modal = document.getElementById("shapediver-success-modal");
		if (!modal) { console.error("Success modal not found!"); return; }

		const closeBtn = modal.querySelector(".shapediver-btn-close-success") as HTMLButtonElement;
		const closeXBtn = modal.querySelector(".shapediver-success-close") as HTMLButtonElement;

		modal.style.display = "flex";

		const hideModal = () => { modal.style.display = "none"; cleanup(); };

		const handleBackdropClick = (e: MouseEvent) => { if (e.target === modal) hideModal(); };
		const handleKeyPress = (e: KeyboardEvent) => { if (e.key === "Escape") hideModal(); };

		const cleanup = () => {
			if (closeBtn) closeBtn.removeEventListener("click", hideModal);
			if (closeXBtn) closeXBtn.removeEventListener("click", hideModal);
			modal.removeEventListener("click", handleBackdropClick);
			document.removeEventListener("keydown", handleKeyPress);
		};

		if (closeBtn) closeBtn.addEventListener("click", hideModal);
		if (closeXBtn) closeXBtn.addEventListener("click", hideModal);
		modal.addEventListener("click", handleBackdropClick);
		document.addEventListener("keydown", handleKeyPress);
	}
}

// ============================================================================
// DEFAULT — SDK FACTORY REGISTRATION
// ============================================================================

(globalThis as {[key: string]: any}).specificECommerceApiActionsFactory = (
	defaultActions: IECommerceApiActions,
) => {
	globalApiActionsInstance = new SpecificECommerceApiActions(defaultActions);
	return globalApiActionsInstance;
};

let globalApiActionsInstance: SpecificECommerceApiActions | null = null;

const urlParams = new URLSearchParams(window.location.search);

export const developmentUrlBuilderOptions: IAppBuilderUrlBuilderData = {
	baseUrl: `https://appbuilder.shapediver.com/v1/main/${urlParams.get("appBuilderVersion") ?? "development"}/`,
	settingsUrl: urlParams.get(QUERYPARAM_SETTINGSURL) ?? "_stringselect.json",
	modelStateId: urlParams.get(QUERYPARAM_MODELSTATEID) ?? undefined,
	slug: urlParams.get(QUERYPARAM_SLUG) ?? undefined,
};

console.log("🏗️ developmentUrlBuilderOptions:", JSON.stringify(developmentUrlBuilderOptions, null, 2));

(globalThis as {[key: string]: any}).developmentUrlBuilderOptions =
	developmentUrlBuilderOptions;

// ============================================================================
// ============================================================================
// CUSTOM CODE — everything below this line is custom to Tara Blooms
// ============================================================================
// ============================================================================

// ============================================================================
// CUSTOM — TYPE DEFINITIONS
// ============================================================================

interface ShapeDiverProductData {
	id: number;
	variation_id?: number;
	name: string;
	variation_name?: string;
	price: string;
	model_view_url: string;
	embedding_ticket: string;
	configurator_url: string;
	model_state_id: string;
	slug: string;
	settings_url: string;
	query_params?: Record<string, string>;
}

interface AjaxResponse<T = any> {
	success: boolean;
	data: T;
}

interface ConfiguratorData {
	ajaxurl: string;
	settings: {
		configurator_url: string;
		default_settings_url: string;
		debug_flag: boolean;
		cart_item_button_label: string;
		cart_item_button_classes: string;
	};
	isLoggedIn: boolean;
	loginUrl: string;
	api_base_url: string;
}

interface WarehouseData {
	warehouse_code: string;
	warehouse_name: string;
	availability_status: string;
	available_quantity: number;
	promise_date: string;
	display_message: string;
	pricing_summary: {
		base_price: number;
		tariff_cost: number;
		shipping_cost: number;
		sales_tax: number;
		total_cost: number;
	};
	markup_pricing_summary: {
		base_price: number;
		tariff_cost: number;
		shipping_cost: number;
		sales_tax: number;
		total_cost: number;
	};
}

// ============================================================================
// CUSTOM — GLOBAL DECLARATIONS
// ============================================================================

declare const jQuery: any;
declare const configuratorData: ConfiguratorData;

declare global {
	function openWarehousePopup(): void;
	function closeWarehousePopup(): void;
	function addWarehouseToCart(warehouse: string): void;
}

// ============================================================================
// CUSTOM — CONSTANTS & CONFIGURATION
// ============================================================================

// const GRAPHICS_API_BASE_URL =
//     `${configuratorData.api_base_url}/wp-json/shapediver/v1/graphic-components`;
const GRAPHICS_API_BASE_URL =
    `https://dev4.tarablooms.in/wp-json/shapediver/v1/graphic-components`;
const CATEGORY_IDENTIFIER = "\u200B\u2063";
const DEFAULT_PAGE_SIZE = 10;
const API_TIMEOUT = 10000;
const MAX_RETRIES = 2;
const RETRY_DELAY = 2000;

class ProductDataManager {
	private static instance: ProductDataManager;
	private productData: ShapeDiverProductData | null = null;
	private loadingPromise: Promise<ShapeDiverProductData | null> | null = null;

	private constructor() {}

	public static getInstance(): ProductDataManager {
		if (!ProductDataManager.instance) {
			ProductDataManager.instance = new ProductDataManager();
		}
		return ProductDataManager.instance;
	}

	public getProductId(): number | null {
		if (this.productData?.id) return this.productData.id;

		const windowProduct = (window as any).shapeDiverCurrentProduct;
		if (windowProduct?.id) return windowProduct.id;

		const windowProductId = (window as any).shapeDiverProductId;
		if (windowProductId) return parseInt(windowProductId);

		const modal = document.getElementById("app-builder-modal-wrapper");
		if (modal && (modal as any).currentProductData?.id)
			return (modal as any).currentProductData.id;

		const button = document.getElementById("app-builder-open-configurator");
		const productId = button?.getAttribute("data-product-id");
		if (productId) return parseInt(productId);

		return null;
	}

	public getProductData(): ShapeDiverProductData | null {
		if (this.productData) return this.productData;

		const windowProduct = (window as any).shapeDiverCurrentProduct;
		if (windowProduct?.id) {
			this.productData = windowProduct;
			return this.productData;
		}

		const modal = document.getElementById("app-builder-modal-wrapper");
		if (modal && (modal as any).currentProductData) {
			this.productData = (modal as any).currentProductData;
			return this.productData;
		}

		return null;
	}

	public async loadProductData(
		productId: string | number,
	): Promise<ShapeDiverProductData | null> {
		if (this.loadingPromise) return this.loadingPromise;

		this.loadingPromise = this._fetchProductData(productId);
		const result = await this.loadingPromise;
		this.loadingPromise = null;
		return result;
	}

	private async _fetchProductData(
		productId: string | number,
	): Promise<ShapeDiverProductData | null> {
		try {
			console.log("🔄 Loading product data for ID:", productId);

			const response = await fetch(configuratorData.ajaxurl, {
				method: "POST",
				headers: {"Content-Type": "application/x-www-form-urlencoded"},
				body: new URLSearchParams({
					action: "get_product_data",
					product_id: productId.toString(),
				}),
			});

			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			const result: AjaxResponse<ShapeDiverProductData> = await response.json();

			if (result.success && result.data) {
				this.setProductData(result.data);
				console.log("✅ Product data loaded:", result.data.name);
				console.log("🔗 query_params from server:", JSON.stringify(result.data.query_params, null, 2));

				return result.data;
			} else {
				throw new Error("Invalid response from server");
			}
		} catch (error) {
			console.error("❌ Failed to load product data:", error);
			return null;
		}
	}

	public setProductData(productData: ShapeDiverProductData): void {
		this.productData = productData;
		(window as any).shapeDiverCurrentProduct = productData;
		(window as any).shapeDiverProductId = productData.id;

		const modal = document.getElementById("app-builder-modal-wrapper");
		if (modal) (modal as any).currentProductData = productData;

		console.log("💾 Product data stored (ID:", productData.id, ")");
	}

	public clear(): void {
		this.productData = null;
		this.loadingPromise = null;
		delete (window as any).shapeDiverCurrentProduct;
		delete (window as any).shapeDiverProductId;

		const modal = document.getElementById("app-builder-modal-wrapper");
		if (modal) delete (modal as any).currentProductData;
	}
}

// ============================================================================
// GRAPHICS API MANAGER (Singleton)
// ============================================================================

class GraphicsApiManager {
	private static instance: GraphicsApiManager;
	private latestQuery: string = "";
	private latestCategorySlugs: Array<string> = [];
	private pageSize: number = DEFAULT_PAGE_SIZE;
	private currentIndex: number = 0;
	private cachedResults: Record<string, IGraphicsApiResponse> = {};
	private resetCacheCallback: (() => void) | null = null;

	private constructor() {
		this.latestQuery = this.createQueryString();
	}

	public static getInstance(): GraphicsApiManager {
		if (!GraphicsApiManager.instance) {
			GraphicsApiManager.instance = new GraphicsApiManager();
		}
		return GraphicsApiManager.instance;
	}

	public createQueryString(
		categories?: string[] | string,
		tags?: string,
		search?: string,
		categoryIds?: number[],
	): string {
		const params = new URLSearchParams();

		if (Array.isArray(categories) && categories.length === 0)
			categories = undefined;
		if (!categories && !tags && !search) categories = "all";

		if (categoryIds && categoryIds.length > 0) {
			params.set("category_id", categoryIds[categoryIds.length - 1].toString());
			if (!tags && !search) params.set("tags", "all");
		} else if (categories) {
			if (Array.isArray(categories))
				params.set("categories", categories.join(","));
			else params.set("categories", categories);
			if (categories !== "all" && !tags && !search)
				params.set("tags", "all");
		}

		if (tags) params.set("tags", tags);
		if (search) params.set("search", search);

		return params.toString();
	}

	public async fetchFromApi(
		queryString: string,
		ignoreCache: boolean = false,
	): Promise<IGraphicsApiResponse> {
		if (!ignoreCache && this.cachedResults[queryString])
			return this.cachedResults[queryString];

		const url = `${GRAPHICS_API_BASE_URL}?${queryString}`;
		const nonce = await getFreshNonce();

		const resp = await fetch(url, {
			method: "GET",
			credentials: "include",
			headers: {
				Accept: "application/json",
				...(nonce ? {"X-WP-Nonce": nonce} : {}),
			},
		});

		if (!resp.ok) {
			const text = await resp.text().catch(() => "");
			throw new Error(
				`Failed to fetch from graphics api: ${resp.status} ${resp.statusText} ${text}`,
			);
		}

		const json = await resp.json().catch((err) => {
			throw new Error(`Failed to parse graphics api response: ${String(err)}`);
		});

		if (!json || typeof json !== "object")
			throw new Error("Invalid graphics api response: not an object");

		const result = json as IGraphicsApiResponse;
		this.cachedResults[queryString] = result;
		return result;
	}

	public clearCache(): void {
		this.latestQuery = this.createQueryString();
		this.latestCategorySlugs = [];
		this.pageSize = DEFAULT_PAGE_SIZE;
		this.currentIndex = 0;

		for (const key in this.cachedResults) delete this.cachedResults[key];
		if (this.resetCacheCallback) this.resetCacheCallback();
	}

	public async setParameters(
		data: IScrollingApiSetParametersData,
	): Promise<IScrollingApiSetParametersReply<unknown>> {
		if (data.source !== "graphics") {
			console.debug(`Unsupported data source name: ${data.source}`);
			this.clearCache();
			return {hasNextPage: false, items: []};
		}

		if (data.reset) this.resetCacheCallback = data.reset;

		if (data.terms) {
			const categories: string[] = [];
			const categoryIds: number[] = [];
			let tags: string = "";
			let search: string = "";

			const allCategoriesQuery = this.createQueryString();
			const categoryData = await this.fetchFromApi(allCategoriesQuery, false);
			let cachedCategories = categoryData.categories;

			data.terms?.forEach((v) => {
				if (v.startsWith(CATEGORY_IDENTIFIER)) {
					if (cachedCategories) {
						const categoryName = v.substring(CATEGORY_IDENTIFIER.length);
						const matchedCategory: ICategory | undefined = cachedCategories.find(
							(cat) => cat.name === categoryName,
						);
						if (matchedCategory) {
							categories.push(matchedCategory.slug);
							categoryIds.push(matchedCategory.id);
							cachedCategories = matchedCategory.children;
						}
					}
				} else if (v.startsWith("tag:")) {
					tags = v.substring("tag:".length);
				} else {
					search = v;
				}
			});

			const queryString = this.createQueryString(categories, tags, search, categoryIds);
			if (queryString !== this.latestQuery) {
				this.latestQuery = queryString;
				this.latestCategorySlugs = categories;
				this.currentIndex = 0;
			}
		}

		if (data.pageSize !== undefined) {
			this.pageSize = data.pageSize;
			this.currentIndex = 0;
		}

		await this.fetchFromApi(this.latestQuery, false);
		return this.returnMappedResults();
	}

	public async loadMore(
		data: IScrollingApiLoadMoreData,
	): Promise<IScrollingApiLoadMoreReply<unknown>> {
		if (data.source !== "graphics") {
			console.debug(`Unsupported data source name: ${data.source}`);
			this.clearCache();
			return {hasNextPage: false, items: []};
		}

		await this.fetchFromApi(this.latestQuery, false);
		return this.returnMappedResults();
	}

	private async returnMappedResults(): Promise<IScrollingApiLoadMoreReply<unknown>> {
		const result: IScrollingApiLoadMoreReply<unknown> = {
			hasNextPage: false,
			items: [],
		};

		// ── Parse active query to detect search/tag mode ──────────────────────
		const activeParams = new URLSearchParams(this.latestQuery);
		const hasSearch    = activeParams.has("search") && activeParams.get("search") !== "";
		const hasTags      = activeParams.has("tags")   && activeParams.get("tags")   !== "all";

		// Only inject category folders on the FIRST page AND only when NOT in
		// search/tag mode. Searching must return products only — no folders.
		const shouldShowFolders = this.currentIndex === 0 && !hasSearch && !hasTags;

		if (shouldShowFolders) {
			const allCategoriesQuery = this.createQueryString();
			const categoryData = await this.fetchFromApi(allCategoriesQuery, false);
			let categories = categoryData.categories;

			if (categories) {
				for (let i = 0; i < this.latestCategorySlugs.length; i++) {
					const categorySlug = this.latestCategorySlugs[i];
					const matchedCategory: ICategory | undefined = categories?.find(
						(cat) => cat.slug === categorySlug,
					);
					if (!matchedCategory)
						throw new Error(`Category with slug "${categorySlug}" not found`);
					categories = matchedCategory.children;
				}

				const items: IScrollingApiItemTypeSelect[] = categories.map((p) => ({
					item: `search:${CATEGORY_IDENTIFIER}` + p.name,
					data: {
						displayname: p.name,
						imageUrl: p.thumbnail_url,
						data: p,
					},
				}));
				result.items = items;
			}
		}

		const products = this.cachedResults[this.latestQuery]?.products;
		if (products) {
			const items: IScrollingApiItemTypeSelect[] = products
				.slice(this.currentIndex, this.currentIndex + this.pageSize)
				.map((p) => ({
					item: p.graphic_id + "",
					data: {
						displayname: "\u200B\u2063",
						tooltip: p.description,
						imageUrl: p.downloadable_files.find((f) =>
							f.sessionId.includes("thumbnail"),
						)?.parameterValues.file_url
							?? p.thumbnail_url
							?? p.image_url,
						data: p,
					},
				}));

			this.currentIndex += this.pageSize;
			result.hasNextPage = this.currentIndex < products.length;
			result.items = result.items.concat(items);
		}

		return result;
	}
}

// ============================================================================
// SETTINGS MANAGER (Singleton)
// ============================================================================

class SettingsManager {
	private static instance: SettingsManager;
	private settings: Record<string, string> = {};
	private loadPromise: Promise<void> | null = null;

	private constructor() {}

	public static getInstance(): SettingsManager {
		if (!SettingsManager.instance) {
			SettingsManager.instance = new SettingsManager();
		}
		return SettingsManager.instance;
	}

	public async load(): Promise<void> {
		if (this.loadPromise) return this.loadPromise;

		this.loadPromise = fetch(configuratorData.ajaxurl, {
			method: "POST",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			body: new URLSearchParams({action: "get_shapediver_settings"}),
		})
			.then((res) => res.json())
			.then((result) => {
				if (result.success && result.data) {
					this.settings = result.data;
					console.log("✅ ShapeDiver settings loaded:", this.settings);
				}
			})
			.catch((err) => {
				console.warn("⚠️ Failed to load ShapeDiver settings, using defaults:", err);
			});

		return this.loadPromise;
	}

	public get(key: string, fallback: string = ""): string {
		return this.settings[key] ?? fallback;
	}

	public getPreferredWarehouse(): string {
		return this.get("preferred_warehouse", "COL");
	}
}

// ============================================================================
// MODAL MANAGER (Singleton)
// ============================================================================

class ShapeDiverModalManager {
	private static instance: ShapeDiverModalManager | null = null;
	private modal: HTMLElement | null = null;
	private closeButton: HTMLElement | null = null;
	private iframe: HTMLIFrameElement | null = null;
	private eventListeners: Map<string, EventListenerOrEventListenerObject> = new Map();

	private constructor() {
		this.init();
	}

	public static getInstance(): ShapeDiverModalManager {
		if (!ShapeDiverModalManager.instance) {
			ShapeDiverModalManager.instance = new ShapeDiverModalManager();
		}
		return ShapeDiverModalManager.instance;
	}

	private init(): void {
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", () => this.setupElements());
		} else {
			this.setupElements();
		}
	}

	private setupElements(): void {
    this.modal = document.getElementById("app-builder-modal-wrapper");
    this.closeButton = document.getElementById("app-builder-modal-close-button");
    this.iframe = document.getElementById("shapediver-iframe-model") as HTMLIFrameElement;  // ← change this

    if (!this.modal || !this.closeButton) {
        console.warn("⚠️ Modal elements not found");
        return;
    }

    this.setupEventListeners();
    console.log("✅ Modal manager initialized");
}

	private setupEventListeners(): void {
		if (!this.modal || !this.closeButton) return;

		this.removeEventListeners();

		const closeHandler = () => this.closeModal();
		this.closeButton.addEventListener("click", closeHandler);
		this.eventListeners.set("closeButton", closeHandler);

		const backdropHandler = (e: Event) => {
			if (e.target === this.modal) this.closeModal();
		};
		this.modal.addEventListener("click", backdropHandler);
		this.eventListeners.set("backdrop", backdropHandler);

		const escHandler = (e: Event) => {
			if ((e as KeyboardEvent).key === "Escape" && this.isModalOpen())
				this.closeModal();
		};
		document.addEventListener("keydown", escHandler);
		this.eventListeners.set("escape", escHandler);
	}

	private removeEventListeners(): void {
		if (this.closeButton && this.eventListeners.has("closeButton")) {
			this.closeButton.removeEventListener(
				"click",
				this.eventListeners.get("closeButton")!,
			);
		}
		if (this.modal && this.eventListeners.has("backdrop")) {
			this.modal.removeEventListener(
				"click",
				this.eventListeners.get("backdrop")! as EventListenerOrEventListenerObject,
			);
		}
		if (this.eventListeners.has("escape")) {
			document.removeEventListener(
				"keydown",
				this.eventListeners.get("escape")! as EventListenerOrEventListenerObject,
			);
		}
		this.eventListeners.clear();
	}

	private isModalOpen(): boolean {
		return this.modal ? this.modal.style.display !== "none" : false;
	}

	public async closeModal(): Promise<void> {
		console.log("🚪 Closing ShapeDiver modal");

		if (globalApiActionsInstance) {
			console.log("💾 Triggering closeConfigurator for auto-save...");
			try {
				await globalApiActionsInstance.closeConfigurator();
				console.log("✅ closeConfigurator completed");
			} catch (err) {
				console.warn("⚠️ closeConfigurator failed:", err);
			}
		} else {
			console.warn("⚠️ No globalApiActionsInstance — save skipped");
		}

		if (typeof (window as any).destroySpecificECommerceApiActions === "function") {
			(window as any).destroySpecificECommerceApiActions();
		}

		if (this.modal) this.modal.style.display = "none";
		if (this.iframe) this.iframe.src = "about:blank";

		delete (window as any).shapeDiverFamilyName;
		console.log("✅ Modal closed, family name cleared");
	}

	public openModal(productData?: ShapeDiverProductData): void {
		console.log("🚀 Opening ShapeDiver modal");

		if (!this.modal) {
			console.error("❌ Modal not found");
			return;
		}

		if (productData) ProductDataManager.getInstance().setProductData(productData);
		this.modal.style.display = "flex";
	}
}

// ============================================================================
// WAREHOUSE MANAGER
// ============================================================================

class WarehouseManager {
	private static formatCurrency(amount: number): string {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
		}).format(amount);
	}

	private static formatDate(dateString: string): string {
		try {
			const [month, day, year] = dateString.split("-");
			const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
			const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
			return `${day}-${months[date.getMonth()]}-${year}`;
		} catch (e) {
			return dateString;
		}
	}

	private static parsePromiseDate(dateString: string): Date {
		const [month, day, year] = dateString.split("-").map((num) => parseInt(num, 10));
		return new Date(year, month - 1, day);
	}

	public static async openPopup(
		warehouses: WarehouseData[],
		pricingParameters: Record<string, any>,
		modelStateId: string,
		productData?: ShapeDiverProductData | null,
		apiResponse?: any,
	): Promise<void> {

		console.log("🛒 catalogMode check:", (window as any).shapediverAjax?.catalogMode);

		await SettingsManager.getInstance().load();
		const PREFERRED_WAREHOUSE = SettingsManager.getInstance().getPreferredWarehouse();

		console.log("🏬 Opening warehouse popup. Preferred warehouse:", PREFERRED_WAREHOUSE);

		if (!pricingParameters.product_id) {
			const productId = ProductDataManager.getInstance().getProductId();
			if (productId) {
				pricingParameters.product_id = productId;
				console.log("✅ Added product_id to pricingParameters:", productId);
			} else {
				console.error("❌ No product_id available!");
				alert("Unable to load pricing information. Product ID not found.\n\nPlease refresh the page and try again.");
				return;
			}
		}

		if (productData) ProductDataManager.getInstance().setProductData(productData);

		(window as any).shapeDiverApiResponse = apiResponse;

		const preferredWarehouse = warehouses.find(
			(w) => w.warehouse_code === PREFERRED_WAREHOUSE,
		);

		let warehousesToDisplay: WarehouseData[];

		if (preferredWarehouse && preferredWarehouse.available_quantity > 0) {
			warehousesToDisplay = [preferredWarehouse];
			console.log("✅ Preferred warehouse in stock — showing only:", PREFERRED_WAREHOUSE);
		} else {
			warehousesToDisplay = warehouses;
			console.log("⚠️ Preferred warehouse out of stock or unavailable — showing all", warehouses.length, "warehouse(s)");
		}

		warehousesToDisplay = warehousesToDisplay.sort((a, b) => {
			const dateA = this.parsePromiseDate(a.promise_date);
			const dateB = this.parsePromiseDate(b.promise_date);
			return dateA.getTime() - dateB.getTime();
		});

		const fastestWarehouse =
			warehousesToDisplay.length > 1 ? warehousesToDisplay[0].warehouse_code : null;
		const cheapestWarehouse =
			warehousesToDisplay.length > 1
				? warehousesToDisplay.reduce((min, w) => {
						const minPrice = (min.markup_pricing_summary || min.pricing_summary).total_cost;
						const wPrice = (w.markup_pricing_summary || w.pricing_summary).total_cost;
						return wPrice < minPrice ? w : min;
					}).warehouse_code
				: null;

		const popup = document.getElementById("warehousePopup");
		if (!popup) return;

		const container = popup.querySelector(".warehouse-container");
		if (!container) return;

		container.innerHTML = "";

		warehousesToDisplay.forEach((warehouse) => {
			const card = this.createWarehouseCard(
				warehouse,
				pricingParameters,
				modelStateId,
				0,
				warehouse.warehouse_code === PREFERRED_WAREHOUSE,
				warehouse.warehouse_code === fastestWarehouse,
				warehouse.warehouse_code === cheapestWarehouse,
			);
			container.appendChild(card);
		});

		const wrapper = popup.querySelector(".warehouse-popup-wrapper");
		if (wrapper) {
			const isPreferredInStock = preferredWarehouse && preferredWarehouse.available_quantity > 0;
			if (isPreferredInStock) {
				wrapper.classList.add("single-warehouse");
				wrapper.classList.remove("multiple-warehouses");
			} else {
				wrapper.classList.add("multiple-warehouses");
				wrapper.classList.remove("single-warehouse");
			}
		}

		popup.classList.add("active");
		document.body.style.overflow = "hidden";

		setTimeout(() => this.setupCloseHandlers(popup), 100);
		this.setupRealtimePricingButton(apiResponse, modelStateId, productData);
	}

	private static setupRealtimePricingButton(
		apiResponse: any,
		modelStateId: string,
		productData?: ShapeDiverProductData | null,
	): void {
		const button = document.getElementById("enable-realtime-pricing-btn") as HTMLButtonElement;
		const messageDiv = document.getElementById("realtime-message") as HTMLElement;

		if (!button) {
			console.warn("Real-time pricing button not found");
			return;
		}

		const newButton = button.cloneNode(true) as HTMLButtonElement;
		button.parentNode?.replaceChild(newButton, button);

		newButton.addEventListener("click", async () => {
			console.log("⚡ ENABLE REAL-TIME PRICING CLICKED");

			const productId = productData?.id || ProductDataManager.getInstance().getProductId();

			if (!productId) {
				console.error("❌ No product ID available");
				this.showMessage(messageDiv, "Product ID not found. Please try again.", "error");
				return;
			}

			if (!apiResponse) {
				console.error("❌ No API response available");
				this.showMessage(messageDiv, "Pricing data not available. Please try again.", "error");
				return;
			}

			newButton.disabled = true;
			const btnText = newButton.querySelector(".btn-text") as HTMLElement;
			const btnLoading = newButton.querySelector(".btn-loading") as HTMLElement;

			if (btnText) btnText.style.display = "none";
			if (btnLoading) btnLoading.style.display = "flex";

			messageDiv.style.display = "none";
			messageDiv.className = "realtime-message";

			try {
				const response = await fetch(configuratorData.ajaxurl, {
					method: "POST",
					headers: {"Content-Type": "application/x-www-form-urlencoded"},
					credentials: "same-origin",
					body: new URLSearchParams({
						action: "enable_realtime_pricing",
						product_id: productId.toString(),
						model_state_id: modelStateId,
						api_response: JSON.stringify(apiResponse),
					}),
				});

				const result = await response.json();
				console.log("✅ Response:", result);

				if (result.success) {
					this.showMessage(
						messageDiv,
						result.data?.message || "Real-time pricing & stock enabled successfully!",
						"success",
					);
					if (btnText) btnText.textContent = "✓ Real-Time Pricing Enabled";
					setTimeout(() => {
						if (btnText) btnText.textContent = "Enable Real Time Pricing & Stock";
					}, 2000);
				} else {
					throw new Error(result.data?.message || "Failed to enable real-time pricing");
				}
			} catch (error: any) {
				console.error("❌ Error:", error);
				this.showMessage(
					messageDiv,
					error.message || "Failed to enable real-time pricing. Please try again.",
					"error",
				);
			} finally {
				newButton.disabled = false;
				if (btnText) btnText.style.display = "inline";
				if (btnLoading) btnLoading.style.display = "none";
			}
		});

		console.log("✅ Real-time pricing button setup complete");
	}

	private static showMessage(
		element: HTMLElement,
		message: string,
		type: "success" | "error",
	): void {
		element.textContent = message;
		element.className = `realtime-message ${type}`;
		element.style.display = "block";

		if (type === "success") {
			setTimeout(() => {
				element.style.display = "none";
			}, 5000);
		}
	}

	private static setupCloseHandlers(popup: HTMLElement): void {
		const closeBtn = popup.querySelector(".warehouse-popup-close") as HTMLButtonElement;
		if (closeBtn) {
			const newCloseBtn = closeBtn.cloneNode(true) as HTMLButtonElement;
			closeBtn.parentNode?.replaceChild(newCloseBtn, closeBtn);
			newCloseBtn.addEventListener("click", (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.closePopup();
			});
		}

		popup.addEventListener("click", (e: MouseEvent) => {
			if (e.target === popup) this.closePopup();
		});
	}

	public static closePopup(): void {
		const popup = document.getElementById("warehousePopup");
		if (popup) {
			popup.classList.remove("active");
			document.body.style.overflow = "";
		}
	}

	private static createWarehouseCard(
    warehouse: WarehouseData,
    pricingParameters: Record<string, any>,
    modelStateId: string,
    _index: number,
    isPreferred: boolean = false,
    isFastest: boolean = false,
    isCheapest: boolean = false,
): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "warehouse-card-wrapper";

    const isCatalogMode: boolean =
        (window as any).shapediverAjax?.catalogMode === true;

    if (isFastest || isCheapest) {
        const badgesContainer = document.createElement("div");
        badgesContainer.className = "warehouse-badges-container";

        if (isFastest) {
            const fastestBadge = document.createElement("span");
            fastestBadge.className = "fastest-badge";
            fastestBadge.innerHTML = "⚡FASTEST";
            badgesContainer.appendChild(fastestBadge);
        }

        if (isCheapest) {
            const affordableBadge = document.createElement("span");
            affordableBadge.className = "affordable-badge";
            affordableBadge.innerHTML = "💰 AFFORDABLE";
            badgesContainer.appendChild(affordableBadge);
        }

        wrapper.appendChild(badgesContainer);
    }

    const card = document.createElement("div");
    card.className = "warehouse-card" + (isPreferred ? " preferred-warehouse" : "");

    const formattedDate = this.formatDate(warehouse.promise_date);
    const pricing = warehouse.markup_pricing_summary || warehouse.pricing_summary;

    // ── Price masking for catalog mode ─────────────────────────────────
    const maskPrice = (amount: number): string =>
        isCatalogMode ? "xxx" : this.formatCurrency(amount);
    // ──────────────────────────────────────────────────────────────────

    card.innerHTML = `
        <div class="warehouse-details">
            <div class="detail-row">
                <span class="detail-label">Expected Date</span>
                <span class="detail-value">${formattedDate}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Available Quantity</span>
                <span class="detail-value ${warehouse.available_quantity === 0 ? "out-of-stock" : ""}">
                    ${warehouse.available_quantity}
                </span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Product Cost</span>
                <span class="detail-value">${maskPrice(pricing.base_price)}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Tariff Cost</span>
                <span class="detail-value">${maskPrice(pricing.tariff_cost)}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Shipping Cost</span>
                <span class="detail-value">${maskPrice(pricing.shipping_cost)}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Sales Tax</span>
                <span class="detail-value">${maskPrice(pricing.sales_tax)}</span>
            </div>
            <div class="detail-row total-row">
                <span class="detail-label"><strong>Total Cost</strong></span>
                <span class="detail-value"><strong>${maskPrice(pricing.total_cost)}</strong></span>
            </div>
        </div>
        ${!isCatalogMode ? `
        <button class="warehouse-add-to-cart-btn" data-warehouse="${warehouse.warehouse_code}">
            ADD TO CART
        </button>
        ` : ''}
    `;

    if (!isCatalogMode) {
        const button = card.querySelector(".warehouse-add-to-cart-btn") as HTMLButtonElement;
        if (button) {
            button.addEventListener("click", () => {
                this.addToCart(
                    warehouse.warehouse_code,
                    warehouse.warehouse_name,
                    pricing.total_cost,
                    pricingParameters,
                    modelStateId,
                    warehouse,
                );
            });
        }
    }

    wrapper.appendChild(card);
    return wrapper;
}

	private static async addToCart(
		warehouseCode: string,
		warehouseName: string,
		totalCost: number,
		pricingParameters: Record<string, any>,
		modelStateId: string,
		warehouseData: WarehouseData,
	): Promise<void> {
		console.log("🛒 ADD TO CART | Warehouse:", warehouseName, "| Total:", totalCost);

		const productId = pricingParameters.product_id
			? parseInt(pricingParameters.product_id)
			: ProductDataManager.getInstance().getProductId();

		if (!productId) {
			console.error("❌ CRITICAL: No product ID found!");
			alert("Unable to add to cart. Product ID not found.\n\nPlease refresh the page and try again.");
			return;
		}

		const variationId = pricingParameters.variation_id ? parseInt(pricingParameters.variation_id) : 0;
		const imageUrl = (window as any).shapeDiverImageUrl || "";
		const apiResponse = (window as any).shapeDiverApiResponse || {};
		const familyName = ((window as any).shapeDiverFamilyName as string | undefined)?.trim() || "";

		const requestData: Record<string, string> = {
			action: "shapediver_add_warehouse_to_cart",
			product_id: productId.toString(),
			variation_id: variationId.toString(),
			quantity: "1",
			warehouse_code: warehouseCode,
			warehouse_name: warehouseName,
			model_state_id: modelStateId,
			design_family_name: familyName,
			total_price: totalCost.toString(),
			api_response: JSON.stringify(apiResponse),
			selected_warehouse_data: JSON.stringify(warehouseData),
			image_url: imageUrl,
			custom_data: JSON.stringify({
				warehouse_code: warehouseCode,
				warehouse_name: warehouseName,
				model_state_id: modelStateId,
				api_response: apiResponse,
				selected_warehouse: warehouseData,
			}),
			// ── Configurator URL fields for View 3D Model button ───────────
			// Read from pricingParameters which were set in addItemToCart()
			sd_configurator_url:          pricingParameters.sd_configurator_url          || "",
			sd_slug:                      pricingParameters.sd_slug                      || "",
			sd_settings_url:              pricingParameters.sd_settings_url              || "",
			sd_parameters_settings_url:   pricingParameters.sd_parameters_settings_url   || "",
			// ──────────────────────────────────────────────────────────────
		};

		const clickedButton = document.querySelector(
			`[data-warehouse="${warehouseCode}"]`,
		) as HTMLButtonElement;
		if (clickedButton) {
			clickedButton.disabled = true;
			clickedButton.textContent = "ADDING...";
		}

		try {
			const response = await fetch(configuratorData.ajaxurl, {
				method: "POST",
				headers: {"Content-Type": "application/x-www-form-urlencoded"},
				credentials: "same-origin",
				body: new URLSearchParams(requestData),
			});

			const result = await response.json();
			console.log("✅ Response:", result);

			if (result.success) {
				this.closePopup();
				const cartUrl = result.data?.cart_url || "/cart/";
				const cartTab = window.open(cartUrl, "_blank");
				if (!cartTab) {
					window.location.href = cartUrl;
				}
				console.log("✅ Cart opened in new tab");
			} else {
				console.error("❌ Failed:", result);
				alert("Failed to add to cart: " + (result.data?.message || result.message || "Unknown error"));
			}
		} catch (error) {
			console.error("❌ Error:", error);
			alert("Network error. Please try again.");
		} finally {
			if (clickedButton) {
				clickedButton.disabled = false;
				clickedButton.textContent = "ADD TO CART";
			}
		}
	}
}

// ============================================================================
// IMAGE UPLOAD
// ============================================================================

async function imageUpload(): Promise<boolean> {
	console.log("🖼️ IMAGE UPLOAD TRIGGERED");

	return new Promise((resolve) => {
		const modal = createImageUploadModal();
		document.body.appendChild(modal);

		setTimeout(() => {
			modal.style.display = "flex";
		}, 10);

		setupImageUploadHandlers(modal, resolve);
	});
}

function createImageUploadModal(): HTMLElement {
	const modal = document.createElement("div");
	modal.id = "shapediver-image-upload-modal";
	modal.className = "shapediver-upload-modal";

	modal.innerHTML = `
        <div class="shapediver-upload-content">
            <div class="shapediver-upload-header">
                <h3>Upload Image</h3>
                <button class="shapediver-upload-close" aria-label="Close">&times;</button>
            </div>
            <div class="shapediver-upload-body">
                <div class="upload-form-group">
                    <label for="image-type-select">
                        Art Type <span class="required">*</span>
                        <span class="upload-tooltip-wrapper">
                            <span class="upload-tooltip-icon" aria-label="Art type information" tabindex="0">?</span>
                        </span>
                    </label>
                    <select id="image-type-select" class="upload-type-select" required>
                        <option value="flat_carve" selected>Flat Carve</option>
                        <option value="flat_shape_carve_addl">Shape Carve</option>
                        <option value="laser_scene">Laser Scene</option>
                        <option value="laser_spot">Laser Spot</option>
						<option value="porcelain_portrait">Porcelain Portrait</option>
                    </select>
                    <div class="upload-error-message" style="display: none;"></div>
                </div>
                <div class="upload-form-group">
                    <label>Image File <span class="required">*</span></label>
                    <div class="upload-dropzone" id="image-dropzone">
                        <input type="file" id="image-file-input" accept="image/jpeg,image/png,image/gif,image/webp" style="display: none;">
                        <div class="upload-dropzone-content">
                            <svg class="upload-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="17 8 12 3 7 8"></polyline>
                                <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                            <p class="upload-text">Drag & drop your image here</p>
                            <p class="upload-subtext">or</p>
                            <button type="button" class="upload-browse-btn">Browse Files</button>
                            <p class="upload-hint">Supported formats: JPEG, PNG, GIF, WebP (Max 10MB)</p>
                        </div>
                        <div class="upload-file-preview" style="display: none;">
                            <img class="upload-preview-image" src="" alt="Preview">
                            <div class="upload-file-info">
                                <p class="upload-file-name"></p>
                                <p class="upload-file-size"></p>
                            </div>
                            <button type="button" class="upload-remove-btn">&times;</button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="shapediver-upload-footer">
                <button type="button" class="shapediver-btn-cancel">Cancel</button>
                <button type="button" class="shapediver-btn-upload" disabled>
                    <span class="btn-text">Upload Image</span>
                    <span class="btn-loading" style="display: none;">
                        <span class="spinner"></span>
                        Uploading...
                    </span>
                </button>
            </div>
        </div>
    `;

	const tooltipEl = document.createElement("div");
	tooltipEl.className = "upload-tooltip-portal";
	tooltipEl.setAttribute("role", "tooltip");
	tooltipEl.innerHTML = `
        <strong>Flat Carved:</strong> Includes skin or deep cut art.<br><br>
        <strong>Shape Carved:</strong> A three-dimensional service, at an additional cost.<br><br>
        <strong>Laser:</strong> A great way to customize any jet black monument.<br><br>
        <strong>Porcelain Portrait:</strong> Images placed onto a porcelain shape and then affixed to the stone.<br><br>
        <em>Note: To prevent delays in fulfillment, please ensure images are suitable for their respective art types.</em>
    `;

	Object.assign(tooltipEl.style, {
		position:  "fixed",
		zIndex:    "2147483647",
		display:   "none",
		width:     "260px",
		pointerEvents: "none",
	});

	document.body.appendChild(tooltipEl);

	const MARGIN = 8;

	function positionTooltip(icon: HTMLElement): void {
		const rect     = icon.getBoundingClientRect();
		const ttHeight = tooltipEl.offsetHeight;

		if (rect.top >= ttHeight + MARGIN) {
			tooltipEl.style.top = `${rect.top - ttHeight - MARGIN}px`;
			tooltipEl.dataset.placement = "above";
		} else {
			tooltipEl.style.top = `${rect.bottom + MARGIN}px`;
			tooltipEl.dataset.placement = "below";
		}

		const centreLeft = rect.left + rect.width / 2 - 130;
		tooltipEl.style.left = `${Math.max(8, Math.min(centreLeft, window.innerWidth - 268))}px`;
	}

	const initObserver = new MutationObserver(() => {
		const icon = modal.querySelector<HTMLElement>(".upload-tooltip-icon");
		if (!icon) return;
		initObserver.disconnect();

		const show = (): void => {
			tooltipEl.style.display = "block";
			requestAnimationFrame(() => requestAnimationFrame(() => {
				positionTooltip(icon);
				tooltipEl.classList.add("upload-tooltip-portal--visible");
			}));
		};

		const hide = (): void => {
			tooltipEl.classList.remove("upload-tooltip-portal--visible");
			const onEnd = (): void => { tooltipEl.style.display = "none"; };
			tooltipEl.addEventListener("transitionend", onEnd, { once: true });
		};

		icon.addEventListener("mouseenter", show);
		icon.addEventListener("focus",      show);
		icon.addEventListener("mouseleave", hide);
		icon.addEventListener("blur",       hide);

		const cleanObserver = new MutationObserver(() => {
			if (!document.contains(modal)) {
				tooltipEl.remove();
				cleanObserver.disconnect();
			}
		});
		cleanObserver.observe(document.body, { childList: true, subtree: true });
	});

	initObserver.observe(document.body, { childList: true, subtree: true });

	return modal;
}

function setupImageUploadHandlers(
	modal: HTMLElement,
	resolve: (value: boolean) => void,
): void {
	const typeSelect = modal.querySelector("#image-type-select") as HTMLSelectElement;
	const fileInput = modal.querySelector("#image-file-input") as HTMLInputElement;
	const dropzone = modal.querySelector("#image-dropzone") as HTMLElement;
	const browseBtn = modal.querySelector(".upload-browse-btn") as HTMLButtonElement;
	const uploadBtn = modal.querySelector(".shapediver-btn-upload") as HTMLButtonElement;
	const cancelBtn = modal.querySelector(".shapediver-btn-cancel") as HTMLButtonElement;
	const closeBtn = modal.querySelector(".shapediver-upload-close") as HTMLButtonElement;
	const removeBtn = modal.querySelector(".upload-remove-btn") as HTMLButtonElement;
	const descriptionInput = modal.querySelector<HTMLTextAreaElement>("#image-description");
	const errorMessage = modal.querySelector(".upload-error-message") as HTMLElement;

	let selectedFile: File | null = null;

	browseBtn?.addEventListener("click", () => fileInput.click());

	fileInput?.addEventListener("change", (e) => {
		const target = e.target as HTMLInputElement;
		const file = target.files?.[0];
		if (file) handleFileSelect(file);
	});

	dropzone?.addEventListener("dragover", (e) => {
		e.preventDefault();
		dropzone.classList.add("dragover");
	});

	dropzone?.addEventListener("dragleave", () => {
		dropzone.classList.remove("dragover");
	});

	dropzone?.addEventListener("drop", (e) => {
		e.preventDefault();
		dropzone.classList.remove("dragover");
		const files = e.dataTransfer?.files;
		if (files && files.length > 0) handleFileSelect(files[0]);
	});

	removeBtn?.addEventListener("click", () => {
		selectedFile = null;
		fileInput.value = "";
		showFilePreview(false);
		validateForm();
	});

	typeSelect?.addEventListener("change", () => {
		errorMessage.style.display = "none";
		validateForm();
	});

	uploadBtn?.addEventListener("click", async () => {
		if (!selectedFile) { showError("Please select an image file"); return; }
		if (!typeSelect.value) { showError("Please select an image type"); return; }
		await handleUpload();
	});

	cancelBtn?.addEventListener("click", () => closeModal(false));
	closeBtn?.addEventListener("click", () => closeModal(false));

	modal.addEventListener("click", (e) => {
		if (e.target === modal) closeModal(false);
	});

	const escHandler = (e: KeyboardEvent) => {
		if (e.key === "Escape") closeModal(false);
	};
	document.addEventListener("keydown", escHandler);

	function handleFileSelect(file: File): void {
		const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
		if (!allowedTypes.includes(file.type)) {
			showError("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
			return;
		}
		if (file.size > 10 * 1024 * 1024) {
			showError("File size must be less than 10MB");
			return;
		}
		selectedFile = file;
		showFilePreview(true, file);
		validateForm();
		errorMessage.style.display = "none";
	}

	function showFilePreview(show: boolean, file?: File): void {
		const dropzoneContent = modal.querySelector(".upload-dropzone-content") as HTMLElement;
		const filePreview = modal.querySelector(".upload-file-preview") as HTMLElement;

		if (show && file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				const previewImg = modal.querySelector(".upload-preview-image") as HTMLImageElement;
				const fileName = modal.querySelector(".upload-file-name") as HTMLElement;
				const fileSize = modal.querySelector(".upload-file-size") as HTMLElement;

				if (previewImg && e.target?.result) previewImg.src = e.target.result as string;
				if (fileName) fileName.textContent = file.name;
				if (fileSize) fileSize.textContent = formatBytes(file.size);
			};
			reader.readAsDataURL(file);

			dropzoneContent.style.display = "none";
			filePreview.style.display = "flex";
		} else {
			dropzoneContent.style.display = "flex";
			filePreview.style.display = "none";
		}
	}

	function validateForm(): void {
		uploadBtn.disabled = selectedFile === null || typeSelect.value === "";
	}

	function showError(message: string): void {
		errorMessage.textContent = message;
		errorMessage.style.display = "block";
	}

	async function handleUpload(): Promise<void> {
		if (!selectedFile) return;

		const btnText = uploadBtn.querySelector(".btn-text") as HTMLElement;
		const btnLoading = uploadBtn.querySelector(".btn-loading") as HTMLElement;

		uploadBtn.disabled = true;
		btnText.style.display = "none";
		btnLoading.style.display = "flex";

		try {
			const description = descriptionInput?.value ?? "";
			const result = await uploadFileToServer(selectedFile, typeSelect.value, description);

			if (result.success) {
				console.log("✅ Upload successful:", result.data);
				alert(`Image uploaded successfully!\n\nType: ${getTypeName(typeSelect.value)}\nFile: ${result.data.original_filename}\nSize: ${formatBytes(result.data.size)}`);
				closeModal(true);
			} else {
				showError("Upload failed. Please try again.");
				uploadBtn.disabled = false;
				btnText.style.display = "inline";
				btnLoading.style.display = "none";
			}
		} catch (error) {
			console.error("❌ Upload error:", error);
			showError("Upload error: " + (error instanceof Error ? error.message : "Unknown error"));
			uploadBtn.disabled = false;
			btnText.style.display = "inline";
			btnLoading.style.display = "none";
		}
	}

	function closeModal(success: boolean): void {
		modal.remove();
		document.removeEventListener("keydown", escHandler);
		resolve(success);
	}

	function getTypeName(value: string): string {
		const types: Record<string, string> = {
			flat_carve: "Flat Carve",
			flat_shape_carve: "Flat / Shape Carve",
			flat_shape_carve_addl: "Flat / Shape Carve add'l",
			laser_scene: "Laser Scene",
			laser_spot: "Laser Spot",
		};
		return types[value] || value;
	}
}

async function uploadFileToServer(
	file: File,
	imageType: string,
	description: string = "",
): Promise<any> {
	const formData = new FormData();
	formData.append("file", file);
	formData.append("image_type", imageType);
	if (description) formData.append("description", description);

	const userId = (window as any).shapediverAjax?.isLoggedIn
		? await getCurrentUserId()
		: null;
	if (userId) formData.append("user_id", userId.toString());

	const apiUrl = `${window.location.origin}/wp-json/shapediver/v1/upload-images`;

	console.log("📤 Uploading to:", apiUrl, "| Type:", imageType, "| File:", file.name);

	const response = await fetch(apiUrl, {
		method: "POST",
		body: formData,
		credentials: "same-origin",
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`HTTP ${response.status}: ${errorText}`);
	}

	return await response.json();
}

async function getCurrentUserId(): Promise<number | null> {
	try {
		const response = await fetch(configuratorData.ajaxurl, {
			method: "POST",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			credentials: "same-origin",
			body: new URLSearchParams({action: "get_user_profile"}),
		});
		const result = await response.json();
		if (result.success && result.data?.id) return parseInt(result.data.id);
	} catch (error) {
		console.error("Failed to get user ID:", error);
	}
	return null;
}

function formatBytes(bytes: number): string {
	if (bytes === 0) return "0 Bytes";
	const k = 1024;
	const sizes = ["Bytes", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

// ============================================================================
// CUSTOM — DOM READY & INITIALIZATION
// ============================================================================

(globalThis as {[key: string]: any}).openWarehousePopup = () =>
	console.log("Static warehouse popup not used");
(globalThis as {[key: string]: any}).closeWarehousePopup = () =>
	WarehouseManager.closePopup();
(globalThis as {[key: string]: any}).addWarehouseToCart = (_warehouse: string) =>
	console.log("Use dynamic add to cart");
(globalThis as {[key: string]: any}).openWarehousePopupDynamic = (
	warehouses: WarehouseData[],
	pricingParameters: Record<string, any>,
	modelStateId: string,
	productData?: ShapeDiverProductData | null,
) => WarehouseManager.openPopup(warehouses, pricingParameters, modelStateId, productData);

(globalThis as {[key: string]: any}).shapeDiverModalManager =
	ShapeDiverModalManager.getInstance();

// ── Pre-fetched family name cache ─────────────────────────────────────────
let cachedLatestFamilyName: string = "";
let familyNamePrefetchDone: boolean = false;

async function prefetchLatestFamilyName(): Promise<void> {
	try {
		const formData = new URLSearchParams();
		formData.append("action", "shapediver_get_latest_family_name");
		const nonce = (window as any).shapediverAjax?.nonce;
		if (nonce) formData.append("nonce", nonce);

		const r = await fetch("/wp-admin/admin-ajax.php", {
			method: "POST",
			credentials: "same-origin",
			headers: {"Content-Type": "application/x-www-form-urlencoded"},
			body: formData.toString(),
		});
		const result = await r.json();
		if (result.success && result.data?.designTitle) {
			cachedLatestFamilyName = result.data.designTitle;
		}
	} catch (err) {
		console.warn("⚠️ prefetchLatestFamilyName failed:", err);
	} finally {
		familyNamePrefetchDone = true;
	}
}

// ============================================================================
// DOM READY — INITIALIZATION
// ============================================================================

document.addEventListener("DOMContentLoaded", function () {
	console.log("🚀 Initializing ShapeDiver configurator");

	SettingsManager.getInstance().load();
	ShapeDiverModalManager.getInstance();

	const productManager = ProductDataManager.getInstance();

	if (!configuratorData.isLoggedIn) {
		console.log("⚠️ User not logged in - setting up login button");
		const loginButton = document.getElementById("shapediver-login-button");
		if (loginButton) {
			loginButton.addEventListener("click", function (e) {
				e.preventDefault();
				const loginUrl =
					loginButton.getAttribute("data-login-url") || configuratorData.loginUrl;
				window.location.href = loginUrl;
			});
		}
		return;
	}

	// ── VIEW 3D MODEL button click handler ────────────────────────────────
	// Uses data-view3d-url (pre-built ShapeDiver URL) when present.
	// Falls back to AJAX permalink lookup for legacy buttons/orders.
	document.addEventListener("click", async function (e: Event) {
		const target = e.target as HTMLElement;
		const btn = target.closest(".shapediver-view-3d-btn") as HTMLElement | null;
		if (!btn) return;

		const context = btn.getAttribute("data-context");
		if (context !== "cart" && context !== "checkout" && context !== "order") return;

		// Skip buttons that have their own inline handler
		if (btn.classList.contains("shapediver-checkout-3d-btn")) return;

		e.preventDefault();
		e.stopImmediatePropagation();

		// ── Priority 1: pre-built ShapeDiver AppBuilder URL ────────────────
		const preBuiltUrl = btn.getAttribute("data-view3d-url");
		if (preBuiltUrl) {
			shapediverOpenView3dIframe(preBuiltUrl);
			return;
		}

		// ── Priority 2: legacy fallback — resolve product permalink via AJAX
		const modelStateId = btn.getAttribute("data-model-state-id");
		const productId    = btn.getAttribute("data-product-id");
		if (!modelStateId || !productId) return;

		// Open blank tab synchronously (must happen in same user-gesture tick)
		const newTab = window.open("", "_blank");

		try {
			const response = await fetch(configuratorData.ajaxurl, {
				method: "POST",
				headers: { "Content-Type": "application/x-www-form-urlencoded" },
				credentials: "same-origin",
				body: new URLSearchParams({ action: "get_product_permalink", product_id: productId }),
			});
			const result = await response.json();
			let targetUrl: string;
			if (result.success && result.data?.permalink) {
				const url = new URL(result.data.permalink);
				url.searchParams.set("modelStateId", modelStateId);
				url.searchParams.set("_hide_menus", "true");
				targetUrl = url.toString();
			} else {
    			targetUrl = `${window.location.origin}/?p=${productId}&modelStateId=${encodeURIComponent(modelStateId)}&_hide_menus=true`;  // ← ADD HERE TOO
			}
			if (newTab && !newTab.closed) {
				newTab.location.href = targetUrl;
			} else {
				window.location.href = targetUrl;
			}
		} catch (err) {
			const fallback = `${window.location.origin}/?p=${productId}&modelStateId=${encodeURIComponent(modelStateId)}`;
			if (newTab && !newTab.closed) {
				newTab.location.href = fallback;
			} else {
				window.location.href = fallback;
			}
		}
	}, true);

	// ── Find configurator button ──────────────────────────────────────────
	const configuratorButton = (
		document.getElementById("custom-sd-open-btn") ||
		document.getElementById("app-builder-open-configurator")
	) as HTMLButtonElement | null;

	if (configuratorButton) {
		const productIdAttr = configuratorButton.getAttribute("data-product-id");
		console.log("✅ Configurator button found:", configuratorButton.id, "| product-id:", productIdAttr);

		if (productIdAttr) {
			productManager.loadProductData(productIdAttr).then((productData) => {
				if (productData) {
					configuratorButton.removeAttribute("disabled");
					console.log("✅ Product data pre-loaded, button enabled");
				}
			});
		}
	} else {
		console.warn("⚠️ Configurator button not found");
	}

	// ── Kick off prefetch immediately ─────────────────────────────────────
	prefetchLatestFamilyName();

	// ── MutationObserver: apply cached family name instantly on modal open ─
	const saveModal  = document.getElementById("shapediver-save-modal");
	const titleInput = document.getElementById("design-title") as HTMLInputElement | null;

	if (saveModal && titleInput) {
		const prefillObserver = new MutationObserver(() => {
			if (saveModal.style.display !== "flex") return;
			if (titleInput.value.trim() !== "") return;

			if (cachedLatestFamilyName) {
				titleInput.value = cachedLatestFamilyName;
				titleInput.dispatchEvent(new Event("input", {bubbles: true}));
				console.log("✅ Observer prefilled (from cache):", titleInput.value);
			} else if (!familyNamePrefetchDone) {
				const waitAndFill = async () => {
					while (!familyNamePrefetchDone) {
						await new Promise(res => setTimeout(res, 100));
					}
					if (
						saveModal.style.display === "flex" &&
						titleInput.value.trim() === "" &&
						cachedLatestFamilyName
					) {
						titleInput.value = cachedLatestFamilyName;
						titleInput.dispatchEvent(new Event("input", {bubbles: true}));
						console.log("✅ Observer prefilled (after prefetch):", titleInput.value);
					}
				};
				waitAndFill();
			}
		});

		prefillObserver.observe(saveModal, {
			attributes: true,
			attributeFilter: ["style"],
		});

		console.log("✅ Modal prefill observer attached");
	}
});

function shapediverOpenView3dIframe(url: string): void {
	const MODAL_ID = "sd-view3d-iframe-modal";
	let modal = document.getElementById(MODAL_ID);

	if (!modal) {
		modal = document.createElement("div");
		modal.id = MODAL_ID;
		modal.style.cssText = [
			"position:fixed",
			"inset:0",
			"z-index:9999999",
			"background:rgba(0,0,0,0.85)",
			"display:none",
			"align-items:center",
			"justify-content:center",
			"padding:16px",
		].join(";");

		modal.innerHTML = `
			<div style="
				position:relative;
				width:100%;
				max-width:1200px;
				height:90vh;
				background:#000;
				border-radius:8px;
				overflow:hidden;
			">
				<button id="sd-view3d-close-btn" style="
					position:absolute;
					top:10px;
					right:12px;
					z-index:10;
					background:rgba(0,0,0,0.55);
					border:none;
					color:#fff;
					font-size:28px;
					line-height:1;
					width:38px;
					height:38px;
					border-radius:50%;
					cursor:pointer;
					display:flex;
					align-items:center;
					justify-content:center;
					backdrop-filter:blur(4px);
				" aria-label="Close">&times;</button>
				<iframe id="sd-view3d-iframe"
					src=""
					allow="camera;microphone;xr-spatial-tracking"
					allowfullscreen
					style="width:100%;height:100%;border:none;display:block;background:#fff;"
				></iframe>
			</div>`;

		document.body.appendChild(modal);

		document.getElementById("sd-view3d-close-btn")!
			.addEventListener("click", (e: MouseEvent) => {
				e.stopPropagation();
				shapediverCloseView3dIframe();
			});

		modal.addEventListener("click", (e: MouseEvent) => {
			if (e.target === modal) shapediverCloseView3dIframe();
		});

		document.addEventListener("keydown", (e: KeyboardEvent) => {
			if (e.key === "Escape" && modal!.style.display === "flex") {
				shapediverCloseView3dIframe();
			}
		});
	}

	const iframe = document.getElementById("sd-view3d-iframe") as HTMLIFrameElement;
	if (iframe) iframe.src = url;
	modal.style.display = "flex";
	document.body.style.overflow = "hidden";
}

function shapediverCloseView3dIframe(): void {
	const modal = document.getElementById("sd-view3d-iframe-modal");
	if (!modal) return;
	const iframe = document.getElementById("sd-view3d-iframe") as HTMLIFrameElement;
	if (iframe) iframe.src = "";   // stops the ShapeDiver session
	modal.style.display = "none";
	document.body.style.overflow = "";
}
function showFamilyNameBeforeOpen(
	existingStateId: string = "",
	prefillTitleOverride: string = "",
): Promise<string | null> {
	return Promise.resolve(prefillTitleOverride.trim() || null);
}

(globalThis as any).showFamilyNameBeforeOpen = showFamilyNameBeforeOpen;